import AWS from 'aws-sdk';
import Auth from '@aws-amplify/auth';
import NetworkService from '../../shared/NetworkService';
import { ROUTES } from './auth.constants';

const authAPI = () => NetworkService.getClient('lambda-client', {
    baseURL: process.env.COGNITO_LAMBDA_BASE_URL,
    useDefaultEnhancers: false,
});

Auth.configure({
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    mandatorySignId: true,
    region: process.env.COGNITO_REGION,
    userPoolId: process.env.COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.COGNITO_APP_CLIENT_ID,
    oauth: {
        domain: process.env.COGNITO_DOMAIN_URL,
        scope: ['email', 'openid', 'phone', 'profile'],
        redirectSignIn: `${process.env.APP_URL}${ROUTES.SIGN_IN}`,
        redirectSignOut: `${process.env.APP_URL}${ROUTES.SIGN_IN}`,
        responseType: 'code',
    },
});

export class UsernameEmptyException extends Error {
    code = 'UsernameEmptyException';

    constructor() {
        super('Username cannot be empty');
    }
}

export class PasswordEmptyException extends Error {
    code = 'PasswordEmptyException';

    constructor() {
        super('Password cannot be empty');
    }
}

export const domainAuthProvider = () => authAPI().get('/federated-identity-providers').then(res => res.data);

export const getFederatedLoginConfigByEmail = async (email = '') => {
    const emailDomain = email.trim().split('@')[1];

    const domain = await domainAuthProvider();
    if (domain[emailDomain]) {
        return () => Auth.federatedSignIn(domain[emailDomain]);
    }

    return null;
};

export const signIn = async ({ username, password, federatedLoginConfig }) => {
    if (!username) throw new UsernameEmptyException();

    if (federatedLoginConfig) return federatedLoginConfig();

    if (!password) throw new PasswordEmptyException();
    return Auth.signIn(username, password);
};

export const confirmSignIn = ({ user, code, mfaType }) => Auth.confirmSignIn(user, code, mfaType);

export const completeNewPassword = ({ user, password, attributes = {} }) => (
    Auth.completeNewPassword(user, password, attributes)
);

export const setupTOTP = ({ user }) => Auth.setupTOTP(user);

export const currentAuthenticatedUser = () => Auth.currentAuthenticatedUser();

export const currentSession = () => Auth.currentSession();

export const signOut = async () => {
    const signOutRes = await Auth.signOut();
    const { credentials } = AWS.config;

    if (credentials instanceof AWS.CognitoIdentityCredentials) {
        credentials.clearCachedId();
    }

    return signOutRes;
};

export const signUp = ({
    username,
    password,
    attributes,
}) => (
    Auth.signUp({ username, password, attributes })
);

export const confirmSignUp = ({ username, code }) => Auth.confirmSignUp(username, code);

export const resendSignUpConfirmation = ({ username }) => Auth.resendSignUp(username);

export const forgotPassword = ({ username }) => Auth.forgotPassword(username);

export const forgotPasswordSubmit = ({
    username,
    code,
    password,
}) => (
    Auth.forgotPasswordSubmit(username, code, password)
);

export const updateAttributes = attributes => (
    Auth.currentAuthenticatedUser()
        .then(user => Auth.updateUserAttributes(user, attributes))
);

export const changePassword = ({ currentPassword, newPassword }) => (
    Auth.currentAuthenticatedUser()
        .then(user => Auth.changePassword(user, currentPassword, newPassword))
);

export const emailExists = email => authAPI().get('/email-validation', {
    params: {
        userPool: process.env.COGNITO_USER_POOL_ID,
        email,
    },
})
    .then(() => false)
    .catch(() => true);

export const passwordPolicies = () => authAPI().get('/password-policies', {
    params: {
        userPoolId: process.env.COGNITO_USER_POOL_ID,
    },
})
    .then(res => res.data);

export const getExchangedToken = async () => {
    const session = await currentSession();
    const idToken = session.getIdToken().getJwtToken();
    const useExchangedToken = !!process.env.OPENID_EXCHANGE_URL;

    if (!useExchangedToken) {
        return idToken;
    }

    const stored = JSON.parse(localStorage.getItem('app_exchanged_token') || '{}');

    let { exchangedToken } = stored;
    if (stored.idToken !== idToken) {
        if (stored.wait) {
            exchangedToken = await new Promise((resolve) => {
                const interval = setInterval(() => {
                    const newStored = JSON.parse(localStorage.getItem('app_exchanged_token') || '{}');
                    if (newStored.exchangedToken) {
                        clearInterval(interval);
                        resolve(newStored.exchangedToken);
                    }
                }, 300);
            });
        } else {
            localStorage.setItem('app_exchanged_token', JSON.stringify({ wait: true }));

            const headers = { Authorization: `Bearer ${idToken}` };
            const res = await fetch(process.env.OPENID_EXCHANGE_URL, { headers });
            const data = await res.json();

            if (res.status !== 200) {
                throw new Error(data.message);
            }

            exchangedToken = data.access_token;
            localStorage.setItem('app_exchanged_token', JSON.stringify({ idToken, exchangedToken }));
        }
    }

    return exchangedToken;
};
